import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Trophy, BarChart3, Table2, TrendingUp } from "lucide-react";
import PageWrapper from "components/PageWrapper";
import Spinner from "components/Spinner";
import { getBomStatusAPI } from "actions/bomActions";
import {
  buildBomAnalysisReport,
  deriveReportVars,
  fmt,
  fmtPct,
} from "utils/bomAnalysisUtils";

/* ── SmartBuy design-system tones (from colors_and_type.css) ──────── */
const DIST_STYLE_MAP = {
  Mouser: { hex: "#005DAA", bg: "bg-[#005DAA]", text: "text-[#005DAA]" },
  Digikey: { hex: "#CC0000", bg: "bg-[#CC0000]", text: "text-[#CC0000]" },
  Element14: { hex: "#FF6600", bg: "bg-[#FF6600]", text: "text-[#FF6600]" },
  "Element 14": { hex: "#FF6600", bg: "bg-[#FF6600]", text: "text-[#FF6600]" },
  Arrow: { hex: "#0B7D72", bg: "bg-[#0B7D72]", text: "text-[#0B7D72]" },
};
const FALLBACK_STYLES = [
  { hex: "#5F47C9", bg: "bg-s360-violet", text: "text-s360-violet" },
  { hex: "#0F6CBD", bg: "bg-s360-info", text: "text-s360-info" },
  { hex: "#107C10", bg: "bg-s360-success", text: "text-s360-success" },
  { hex: "#BC4B09", bg: "bg-s360-warning", text: "text-s360-warning" },
];
const buildDistStyles = (distNames) => {
  const map = {};
  let fallbackIdx = 0;
  for (const name of distNames) {
    map[name] = DIST_STYLE_MAP[name] || FALLBACK_STYLES[fallbackIdx++ % FALLBACK_STYLES.length];
  }
  return map;
};
const wmShort = (name) => (name || "?").replace(/[^a-zA-Z0-9]/g, "").slice(0, 3).toUpperCase();

const ACCENT_BAR = {
  violet: "from-s360-violet to-s360-violet-300",
  teal: "from-s360-teal to-[#5EE7DA]",
  green: "from-s360-success to-[#4CAF50]",
  amber: "from-s360-warning to-[#FFB000]",
};

/* ── shared presentational pieces ──────────────────────────────────── */
const DistBadge = ({ name, style, size = "sm" }) => (
  <span className="inline-flex items-center gap-1.5">
    <span
      className={`inline-flex items-center justify-center rounded-[4px] font-bold text-white shrink-0 ${style.bg} ${
        size === "sm" ? "w-5 h-5 text-[8px]" : "w-6 h-6 text-[9px]"
      }`}
    >
      {wmShort(name)}
    </span>
    <span className={`font-semibold ${style.text}`}>{name}</span>
  </span>
);

const WinBar = ({ segments, height = "h-2" }) => (
  <div className={`w-full ${height} rounded-full bg-s360-bg2 overflow-hidden flex`}>
    {segments.map((seg, i) => (
      <span key={i} style={{ width: `${seg.pct}%`, background: seg.color }} className="h-full" />
    ))}
  </div>
);

const StatCard = ({ tone = "violet", label, value, valueColor = "text-s360-ink", sub }) => (
  <div className="relative bg-white rounded-md shadow-sm overflow-hidden">
    <span className={`absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r ${ACCENT_BAR[tone]}`} />
    <div className="px-5 py-4 flex flex-col gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-s360-fg3">{label}</span>
      <span className={`font-mono text-[30px] leading-[36px] font-bold tabular-nums ${valueColor}`}>{value}</span>
      {sub && <span className="text-xs text-s360-fg3">{sub}</span>}
    </div>
  </div>
);

const SectionHead = ({ eyebrow, title, emphasis, icon: Icon }) => (
  <div className="flex items-center gap-3 pb-3 mb-5 border-b-2 border-s360-ink">
    {Icon && (
      <span className="w-9 h-9 rounded-md bg-s360-violet-50 text-s360-violet inline-flex items-center justify-center shrink-0">
        <Icon className="w-[18px] h-[18px]" />
      </span>
    )}
    <div className="flex items-baseline gap-2 flex-wrap">
      <span className="text-[11px] font-semibold text-s360-fg3 tracking-[0.1em]">{eyebrow}</span>
      <h2 className="text-2xl font-bold text-s360-ink m-0">
        {title} <em className="not-italic text-s360-violet">{emphasis}</em>
      </h2>
    </div>
  </div>
);

const InsightBanner = ({ label, children }) => (
  <div
    className="rounded-lg p-7 md:p-8"
    style={{ background: "linear-gradient(135deg, #191334 0%, #2b1d6e 100%)" }}
  >
    <span className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-s360-teal">{label}</span>
    <p className="text-white text-lg leading-relaxed max-w-2xl mt-3 mb-0">{children}</p>
  </div>
);

const StatLine = ({ label, value, green, big }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-s360-bg2 last:border-0">
    <span className="text-[11px] text-s360-fg3 uppercase tracking-[0.04em]">{label}</span>
    <span
      className={`font-mono font-semibold tabular-nums text-right ${green ? "text-s360-success" : "text-s360-fg1"} ${
        big ? "text-lg" : "text-[13px]"
      }`}
    >
      {value}
    </span>
  </div>
);

const DuelCard = ({ d1, d2, style1, style2, count, w1, w2, t1, t2, mix }) => {
  const total = w1 + w2;
  const winner = w1 >= w2 ? d1 : d2;
  const loser = winner === d1 ? d2 : d1;
  const wT = winner === d1 ? t1 : t2;
  const lT = winner === d1 ? t2 : t1;
  const wW = winner === d1 ? w1 : w2;
  const lW = winner === d1 ? w2 : w1;
  const pct1 = total > 0 ? Math.round((w1 / total) * 100) : 0;
  const pct2 = total > 0 ? Math.round((w2 / total) * 100) : 0;
  const winPct = total > 0 ? Math.round((wW / total) * 100) : 0;

  return (
    <div className="bg-white rounded-md shadow-sm p-5 flex flex-col gap-3">
      <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-s360-fg3">
        {count} shared parts · unit price only
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        <DistBadge name={d1} style={style1} />
        <span className="text-xs text-s360-fg3">vs</span>
        <DistBadge name={d2} style={style2} />
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex justify-between text-[10px] text-s360-fg3">
          <span>{d1} wins: {w1}</span>
          <span>{d2} wins: {w2}</span>
        </div>
        <WinBar segments={[{ pct: pct1, color: style1.hex }, { pct: pct2, color: style2.hex }]} />
      </div>
      <div className="flex flex-col">
        <StatLine label={`${d1} total`} value={fmt(t1)} />
        <StatLine label={`${d2} total`} value={fmt(t2)} />
        <StatLine label="Best-mix total" value={fmt(mix)} green big />
        <StatLine label={`Saved vs ${loser}`} value={`${fmt(lT - mix)} (${fmtPct(lT - mix, lT)})`} green />
        <StatLine label={`Saved vs ${winner}`} value={`${fmt(wT - mix)} (${fmtPct(wT - mix, wT)})`} green />
      </div>
      <div className="text-xs bg-s360-success-bg text-s360-success-ink rounded-md px-3 py-2">
        <strong>{winner} wins {winPct}%</strong> — {loser} cheaper on {lW} parts.
      </div>
    </div>
  );
};

const IntelligentReportPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getBomStatusAPI(id).then((res) => {
      if (!res.status) {
        setLoading(false);
        return;
      }
      setReportData(buildBomAnalysisReport(res));
      setLoading(false);
    });
  }, [id]);

  const {
    rows,
    winCounts,
    worstTotal,
    partsCompared,
    optimalTotal,
    optimalCoverageCount,
    distNames,
    distSavings,
    bomName,
    totalParts,
    sortedDistsDesc,
    mostExpDist,
    secondExpDist,
    savedVsWorst,
    savedVsMostExp,
    savedVsSecondExp,
    totalWins,
    getDuel,
  } = deriveReportVars(reportData);

  const distStyles = buildDistStyles(distNames);

  // Distributor with the most part-level wins — used to keep the narrative
  // callouts data-driven instead of hardcoding a distributor name.
  const [topWinnerName, topWinnerCount] = Object.entries(winCounts).sort((a, b) => b[1] - a[1])[0] || [null, 0];
  const otherWinsCount = totalWins - topWinnerCount;
  const otherWinsSavings = rows
    .filter((r) => r.winner !== topWinnerName)
    .reduce((a, r) => a + r.save, 0);

  const cheapestDist = sortedDistsDesc[sortedDistsDesc.length - 1]?.distributor;
  const pricierDist = mostExpDist?.distributor;
  const chart1Items = rows.filter((r) => r.winner === cheapestDist && r.loser === pricierDist);
  const chart1Total = chart1Items.reduce((a, r) => a + r.save, 0);
  const chart1MaxT = chart1Items.reduce((m, r) => Math.max(m, r.lPrice * r.qty), 1);

  const gapBuckets = [
    { label: "<5%", min: 0, max: 5, color: "#94a3b8" },
    { label: "5–15%", min: 5, max: 15, color: "#7aa4f0" },
    { label: "15–30%", min: 15, max: 30, color: "#0F6CBD" },
    { label: "30–60%", min: 30, max: 60, color: "#5F47C9" },
    { label: ">60%", min: 60, max: Infinity, color: "#310086" },
  ].map((b) => ({ ...b, n: rows.filter((r) => r.savePct >= b.min && r.savePct < b.max).length }));
  const gapMaxN = Math.max(...gapBuckets.map((b) => b.n), 1);
  const bigGapCount = rows.filter((r) => r.savePct >= 30).length;

  const pairList = [];
  for (let i = 0; i < distNames.length; i++)
    for (let j = i + 1; j < distNames.length; j++) pairList.push([distNames[i], distNames[j]]);

  return (
    <PageWrapper>
      <div className="flex flex-col gap-4">
        {/* Page head */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
              Distributor price intelligence report
            </h1>
            <span className="text-sm text-s360-fg3">
              {bomName} · {totalParts} parts · distributors: {distNames.join(", ") || "—"}
            </span>
          </div>
        </div>

        {loading && (
          <div className="py-16">
            <Spinner label="Loading BOM data…" />
          </div>
        )}

        {!loading && (
          <>
            {/* Hero — headline + methodology */}
            <div
              className="rounded-lg shadow-md overflow-hidden px-6 py-5 md:px-7 md:py-6"
              style={{ background: "linear-gradient(135deg, #191334 0%, #2b1d6e 100%)" }}
            >
              <p className="text-white text-base md:text-lg leading-relaxed max-w-2xl m-0">
                Honest, part-by-part comparison of{" "}
                <em className="not-italic text-s360-teal">what each distributor actually charges.</em>
              </p>
              <p className="text-white/60 text-xs mt-4 leading-relaxed max-w-3xl m-0">
                <strong className="text-white/80">Methodology:</strong> all comparisons use unit price × required
                quantity only. MOQ totals excluded. Only parts where 2+ distributors quote a price are compared. No
                extrapolation.
              </p>
            </div>

            {/* KPI band */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard
                tone="teal"
                label="Parts compared"
                value={String(partsCompared)}
                valueColor="text-s360-info"
                sub="Parts with prices from 2+ distributors"
              />
              <StatCard
                tone="green"
                label="Saved vs worst distributor"
                value={fmt(savedVsWorst)}
                valueColor="text-s360-success"
                sub="Best mix vs always picking the costliest"
              />
              <StatCard
                tone="green"
                label={`Saved vs ${mostExpDist?.distributor ?? "costliest"}-only`}
                value={fmt(savedVsMostExp)}
                valueColor="text-s360-success"
                sub={`${fmtPct(savedVsMostExp, mostExpDist?.totalPrice)} saving`}
              />
              <StatCard
                tone="green"
                label={`Saved vs ${secondExpDist?.distributor ?? "second"}-only`}
                value={fmt(savedVsSecondExp)}
                valueColor="text-s360-success"
                sub={`${fmtPct(savedVsSecondExp, secondExpDist?.totalPrice)} saving`}
              />
            </div>

            {/* ── §01 HEAD-TO-HEAD ── */}
            <div>
              <SectionHead eyebrow="§ 01" title="Distributor vs distributor" emphasis="head-to-head" icon={Trophy} />

              {/* All distributors combined */}
              <div className="bg-white rounded-md shadow-sm p-6 md:p-7 mb-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-s360-fg3 mb-3">
                      All {distNames.length} distributors · {partsCompared} parts
                    </div>
                    <div className="flex items-center gap-2.5 flex-wrap mb-4">
                      {distNames.map((d, i) => (
                        <React.Fragment key={d}>
                          <DistBadge name={d} style={distStyles[d]} size="lg" />
                          {i < distNames.length - 1 && <span className="text-xs text-s360-fg3">+</span>}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="flex justify-between text-[10px] text-s360-fg3 mb-1">
                      {distNames.map((d) => (
                        <span key={d}>
                          {d}: {winCounts[d] || 0}
                        </span>
                      ))}
                    </div>
                    <WinBar
                      height="h-2.5"
                      segments={distNames.map((d) => ({
                        pct: totalWins > 0 ? Math.round(((winCounts[d] || 0) / totalWins) * 100) : 0,
                        color: distStyles[d]?.hex,
                      }))}
                    />
                  </div>
                  <div className="flex flex-col">
                    {sortedDistsDesc.map((d) => (
                      <StatLine key={d.distributor} label={`${d.distributor}-only total`} value={fmt(d.totalPrice)} />
                    ))}
                    <div className="border-t-2 border-s360-ink pt-2 mt-1 flex flex-col">
                      <StatLine label="✦ Best-mix total (all)" value={fmt(optimalTotal)} green big />
                      {sortedDistsDesc.map((d) => {
                        const sv = distSavings[d.distributor] || 0;
                        return (
                          <StatLine
                            key={d.distributor}
                            label={`Saved vs ${d.distributor}-only`}
                            value={`${fmt(sv)} (${fmtPct(sv, d.totalPrice)})`}
                            green
                          />
                        );
                      })}
                      <StatLine
                        label="Saved vs worst per part"
                        value={`${fmt(savedVsWorst)} (${fmtPct(savedVsWorst, worstTotal)})`}
                        green
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Pairwise duels */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
                {pairList.map(([d1, d2]) => {
                  const duel = getDuel(d1, d2);
                  return (
                    <DuelCard
                      key={`${d1}|${d2}`}
                      d1={d1}
                      d2={d2}
                      style1={distStyles[d1]}
                      style2={distStyles[d2]}
                      count={duel.count}
                      w1={duel.w1}
                      w2={duel.w2}
                      t1={duel.t1}
                      t2={duel.t2}
                      mix={duel.mix}
                    />
                  );
                })}
              </div>

              {topWinnerName && (
                <InsightBanner label="The pattern">
                  <strong className="text-s360-teal font-normal">{topWinnerName}</strong> wins the majority of
                  comparisons ({topWinnerCount} of {totalWins}) — but the{" "}
                  <em className="not-italic text-s360-teal">{otherWinsCount} parts</em> where another distributor is
                  cheaper are worth {fmt(otherWinsSavings)} on their own. Routing everything to one distributor costs
                  you on every exception.
                </InsightBanner>
              )}
            </div>

            {/* ── §02 PRICE SPREAD ── */}
            <div>
              <SectionHead eyebrow="§ 02" title="Price spread" emphasis="visualised" icon={BarChart3} />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                {/* Chart 1: Where cheapest beats priciest */}
                <div className="bg-white rounded-md shadow-sm p-6">
                  <div className="text-lg font-semibold text-s360-ink mb-1">
                    Where {cheapestDist ?? "winner"} beats {pricierDist ?? "loser"}
                  </div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-s360-fg3 mb-4">
                    Total cost per part · {cheapestDist} winner vs {pricierDist} price
                  </div>
                  <div className="flex gap-4 text-[10px] text-s360-fg3 mb-2 pl-[108px]">
                    <span style={{ color: distStyles[cheapestDist]?.hex }}>■ {cheapestDist}</span>
                    <span style={{ color: distStyles[pricierDist]?.hex }}>■ {pricierDist}</span>
                  </div>
                  {chart1Items.slice(0, 9).map((r, i) => {
                    const mW = Math.max(4, Math.round((r.wPrice * r.qty / chart1MaxT) * 100));
                    const dW = Math.max(4, Math.round((r.lPrice * r.qty / chart1MaxT) * 100));
                    return (
                      <div key={i} className="flex items-center gap-2.5 mb-2.5 text-[11px]">
                        <span
                          className="w-[100px] shrink-0 text-[10px] text-s360-fg3 whitespace-nowrap overflow-hidden text-ellipsis"
                          title={r.part}
                        >
                          {r.part}
                        </span>
                        <div className="flex-1 flex flex-col gap-0.5">
                          <div
                            className="h-2.5 rounded-sm"
                            style={{ width: `${mW}%`, background: distStyles[cheapestDist]?.hex, opacity: 0.75, minWidth: 4 }}
                          />
                          <div
                            className="h-2.5 rounded-sm"
                            style={{ width: `${dW}%`, background: distStyles[pricierDist]?.hex, opacity: 0.3, minWidth: 4 }}
                          />
                        </div>
                        <span className="w-14 shrink-0 text-[10px] text-s360-success text-right">${r.save.toFixed(0)}</span>
                      </div>
                    );
                  })}
                  <div className="pl-[108px] mt-2 text-[10px] font-semibold text-s360-success">
                    Total saved vs {pricierDist}: {fmt(chart1Total)}
                  </div>
                </div>

                {/* Chart 2: Who wins each part */}
                <div className="bg-white rounded-md shadow-sm p-6">
                  <div className="text-lg font-semibold text-s360-ink mb-1">Who wins each part?</div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-s360-fg3 mb-5">
                    Parts where each distributor has the lowest unit price · {partsCompared} parts total
                  </div>
                  {distNames.map((name) => {
                    const wins = winCounts[name] || 0;
                    const pct = totalWins > 0 ? Math.round((wins / totalWins) * 100) : 0;
                    return (
                      <div key={name} className="flex items-center gap-3.5 mb-4">
                        <span className="w-20 shrink-0 text-xs font-medium" style={{ color: distStyles[name]?.hex }}>
                          {name}
                        </span>
                        <div className="flex-1 h-7 rounded bg-s360-bg2 overflow-hidden">
                          <div
                            className="h-full rounded flex items-center pl-2.5 text-[11px] font-medium text-white"
                            style={{ width: `${pct}%`, background: distStyles[name]?.hex, minWidth: wins > 0 ? 32 : 0 }}
                          >
                            {wins > 0 ? `${wins} parts` : ""}
                          </div>
                        </div>
                        <span className="w-9 shrink-0 text-right text-[13px] font-semibold" style={{ color: distStyles[name]?.hex }}>
                          {pct}%
                        </span>
                      </div>
                    );
                  })}
                  {topWinnerName && (
                    <div className="mt-6 p-4 rounded bg-s360-bg2 text-xs text-s360-fg3">
                      {topWinnerName} dominates — but the <strong className="text-s360-ink">{otherWinsCount} parts</strong>{" "}
                      where it loses are worth <strong className="text-s360-violet">{fmt(otherWinsSavings)}</strong> if you
                      ignore them.
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                {/* Chart 3: Total BOM cost — single vs mixed */}
                <div className="bg-white rounded-md shadow-sm p-6">
                  <div className="text-lg font-semibold text-s360-ink mb-1">Total BOM cost — single vs mixed</div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-s360-fg3 mb-5">
                    Each bar covers only parts that distributor prices · unit price × req qty
                  </div>
                  {(() => {
                    const maxVal = Math.max(optimalTotal, ...sortedDistsDesc.map((d) => d.totalPrice), 1);
                    const barRows = [
                      ...sortedDistsDesc.map((d) => ({
                        label: `${d.distributor} only`,
                        sub: `${d.availableCount ?? "?"} parts`,
                        color: distStyles[d.distributor]?.hex,
                        w: Math.max(4, Math.round((d.totalPrice / maxVal) * 100)),
                        val: fmt(d.totalPrice),
                        bold: false,
                      })),
                      {
                        label: "Best mix ✦",
                        sub: `${optimalCoverageCount} parts`,
                        color: "#107C10",
                        w: Math.max(4, Math.round((optimalTotal / maxVal) * 100)),
                        val: fmt(optimalTotal),
                        bold: true,
                      },
                    ];
                    return barRows.map((r, i) => (
                      <div key={i} className="flex items-center gap-3 mb-4">
                        <div className="w-[100px] shrink-0">
                          <div className={`text-xs ${r.bold ? "font-semibold" : ""}`} style={{ color: r.color }}>
                            {r.label}
                          </div>
                          {r.sub && <div className="text-[9px] text-s360-fg3 mt-0.5">{r.sub}</div>}
                        </div>
                        <div className={`flex-1 h-8 rounded overflow-hidden ${r.bold ? "bg-s360-success-bg" : "bg-s360-bg2"}`}>
                          <div
                            className="h-full rounded flex items-center pl-2.5 text-[11px] text-white"
                            style={{ width: `${r.w}%`, background: r.color, opacity: r.bold ? 1 : 0.8, minWidth: 40 }}
                          >
                            {r.val}
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                  <div className="flex gap-5 flex-wrap mt-2">
                    {sortedDistsDesc.map((d) => (
                      <div key={d.distributor} className="text-[11px]">
                        <span className="text-s360-fg3">vs {d.distributor}</span>{" "}
                        <strong className="text-s360-success">−{fmt(distSavings[d.distributor] || 0)} saved</strong>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Chart 4: Price gap distribution */}
                <div className="bg-white rounded-md shadow-sm p-6">
                  <div className="text-lg font-semibold text-s360-ink mb-1">Price gap distribution</div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-s360-fg3 mb-5">
                    How large is the gap between cheapest and costliest distributor per part
                  </div>
                  {gapBuckets.map((b, i) => (
                    <div key={i} className="flex items-center gap-2.5 mb-3">
                      <span className="w-12 shrink-0 text-[10px] text-s360-fg3">{b.label}</span>
                      <div className="flex-1 h-6 rounded bg-s360-bg2 overflow-hidden">
                        <div
                          className="h-full rounded flex items-center pl-2 text-[10px] text-white"
                          style={{ width: `${Math.max(4, Math.round((b.n / gapMaxN) * 100))}%`, background: b.color, minWidth: b.n > 0 ? 24 : 0 }}
                        >
                          {b.n > 0 ? `${b.n} comparisons` : ""}
                        </div>
                      </div>
                      <span className="w-6 shrink-0 text-right text-[11px] font-semibold text-s360-ink">{b.n}</span>
                    </div>
                  ))}
                  <div className="mt-3 text-xs text-s360-fg3">
                    <strong className="text-s360-violet">
                      {bigGapCount} of {rows.length} comparisons
                    </strong>{" "}
                    show a &gt;30% price gap for the same part.
                  </div>
                </div>
              </div>
            </div>

            {/* ── §03 FULL TABLE ── */}
            <div>
              <SectionHead eyebrow="§ 03" title="Every comparison," emphasis="every part" icon={Table2} />
              <div className="bg-white rounded-md shadow-sm overflow-hidden mb-4">
                <div className="px-6 py-4 border-b border-s360-stroke1 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <h3 className="text-lg font-semibold text-s360-ink m-0">Part-by-part unit price comparison</h3>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-s360-fg3 m-0 mt-0.5">
                      {rows.length} comparisons across {partsCompared} parts · sorted by total saving desc
                    </p>
                  </div>
                  <p className="text-[10px] text-s360-fg3 m-0">unit price × req qty only</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-[12.5px] min-w-[900px]">
                    <thead>
                      <tr>
                        {[
                          { t: "#", r: false },
                          { t: "Part number", r: false },
                          { t: "Req qty", r: true },
                          { t: "Winner", r: false },
                          { t: "Winner $/unit", r: true },
                          { t: "Loser", r: false },
                          { t: "Loser $/unit", r: true },
                          { t: "Unit Δ", r: true },
                          { t: "Total saving", r: true },
                          { t: "% saved", r: true },
                        ].map((h, i) => (
                          <th
                            key={i}
                            className={`px-3.5 py-2.5 bg-s360-bg2 border-b border-s360-stroke1 text-[9.5px] font-semibold uppercase tracking-[0.1em] text-s360-fg3 whitespace-nowrap sticky top-0 ${
                              h.r ? "text-right" : "text-left"
                            }`}
                          >
                            {h.t}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const barW = Math.min(Math.round(r.savePct * 1.2), 80);
                        const wStyle = distStyles[r.winner] || FALLBACK_STYLES[0];
                        return (
                          <tr key={i} className="border-b border-s360-bg2 hover:bg-s360-stripe">
                            <td className="px-3.5 py-2.5 text-right text-[10px] text-s360-fg3">{i + 1}</td>
                            <td className="px-3.5 py-2.5 font-mono text-[11px] text-s360-fg1 whitespace-nowrap">{r.part}</td>
                            <td className="px-3.5 py-2.5 text-right font-mono text-s360-fg1 tabular-nums">
                              {r.qty.toLocaleString()}
                            </td>
                            <td className="px-3.5 py-2.5">
                              <DistBadge name={r.winner} style={wStyle} />
                            </td>
                            <td className="px-3.5 py-2.5 text-right font-mono text-s360-fg1 tabular-nums">
                              ${r.wPrice.toFixed(4)}
                            </td>
                            <td className="px-3.5 py-2.5">
                              <DistBadge name={r.loser} style={distStyles[r.loser] || FALLBACK_STYLES[1]} />
                            </td>
                            <td className="px-3.5 py-2.5 text-right font-mono text-s360-violet tabular-nums">
                              ${r.lPrice.toFixed(4)}
                            </td>
                            <td className="px-3.5 py-2.5 text-right font-mono text-s360-violet tabular-nums">
                              ${(r.lPrice - r.wPrice).toFixed(4)}
                            </td>
                            <td className="px-3.5 py-2.5 text-right font-mono font-semibold text-s360-success tabular-nums whitespace-nowrap">
                              ${r.save.toFixed(2)}
                              <span
                                className="inline-block h-1 rounded-sm bg-s360-success align-middle ml-1.5"
                                style={{ width: barW }}
                              />
                            </td>
                            <td className="px-3.5 py-2.5 text-right font-mono font-semibold text-s360-success tabular-nums">
                              {r.savePct.toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-s360-violet-50 border-t-2 border-s360-violet-100">
                        <td colSpan={8} className="px-3.5 py-3 text-[11px] font-bold text-s360-violet-900">
                          Total identifiable savings (unit price basis)
                        </td>
                        <td className="px-3.5 py-3 text-right text-[13px] font-bold text-s360-success tabular-nums">
                          ${rows.reduce((a, r) => a + r.save, 0).toFixed(2)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            {/* ── §04 AT SCALE ── */}
            <div>
              <SectionHead eyebrow="§ 04" title="What this means" emphasis="at scale" icon={TrendingUp} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {[
                  { lbl: "12 BOMs / year", n: 12, sub: `annual saving vs ${mostExpDist?.distributor ?? "costliest"}-only` },
                  { lbl: "24 BOMs / year", n: 24, sub: "bi-weekly procurement cadence" },
                  { lbl: "52 BOMs / year", n: 52, sub: "weekly runs, compounding across product lines" },
                ].map((k) => (
                  <div key={k.lbl} className="bg-white rounded-md shadow-sm p-6">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-s360-fg3 mb-2.5">
                      {k.lbl}
                    </div>
                    <div className="font-mono text-[32px] leading-9 font-bold text-s360-success mb-1.5 tabular-nums">
                      {fmt(savedVsMostExp * k.n)}
                    </div>
                    <div className="text-xs text-s360-fg3">
                      {fmt(savedVsMostExp)} × {k.n} — {k.sub}
                    </div>
                  </div>
                ))}
              </div>

              {mostExpDist && (
                <InsightBanner label="Honest bottom line">
                  On unit prices alone, mixing distributors saves{" "}
                  <em className="not-italic text-s360-teal">{fmt(savedVsMostExp)} per BOM run</em> vs using{" "}
                  {mostExpDist.distributor} only.
                  {topWinnerName && (
                    <>
                      {" "}
                      {topWinnerName} is the default winner — but the {otherWinsCount} parts where another
                      distributor is cheaper account for {fmt(otherWinsSavings)} of the gain.
                    </>
                  )}{" "}
                  Without automation, a buyer will never know which parts those are on each new BOM.
                </InsightBanner>
              )}
            </div>
          </>
        )}
      </div>
    </PageWrapper>
  );
};

export default IntelligentReportPage;
